// src/pageComponents/configurations/store-details/StoreMetaStep.jsx
import React, { useEffect, useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
    Box,
    Grid,
    Button,
    Typography,
    Stack,
} from "@mui/material";
import PageHeader from "../../../components/page-layout/PageHeader";
import { grey } from "@mui/material/colors";
import FormikTextField from "../../../components/form/FormikTextField";

const validationSchema = Yup.object({
    acceptanceLimit: Yup.number()
        .min(10, "Must be equal or more than 10")
        .required("Acceptance Limit is required"),
    rejectLimit: Yup.number()
        .min(0, "Must be equal or more than 0")
        .required("Reject Limit is required"),
    rejectLimitAfterAccept: Yup.number()
        .min(0, "Must be equal or more than 0")
        .required("Reject Limit (After Accept) is required"),
    requestRejectCount: Yup.number()
        .min(0, "Must be zero or more")
        .required("Request Reject Count is required"),
    acceptRequestRejectCount: Yup.number()
        .min(0, "Must be zero or more")
        .required("Accept Request Reject Count is required"),
    rejectPenaltyAmount: Yup.number()
        .min(0, "Must be zero or more")
        .required("Reject Penalty Amount is required"),
    acceptedRejectPenaltyAmount: Yup.number()
        .min(0, "Must be zero or more")
        .required("Accepted Reject Penalty Amount is required"),
});

export default function StoreMetaStep({ formData = {}, setFormData, nextStep, onBack }) {
    const [initialValues, setInitialValues] = useState({
        acceptanceLimit: "",
        rejectLimit: "",
        rejectLimitAfterAccept: "",
        requestRejectCount: 0,
        acceptRequestRejectCount: 0,
        rejectPenaltyAmount: 0,
        acceptedRejectPenaltyAmount: 0,
    });

    useEffect(() => {
        if (formData.metaLimits) {
            setInitialValues(formData.metaLimits);
        }
    }, [formData.metaLimits]);

    return (
        <Box>
            <PageHeader
                title="Store Meta Information"
                lead="Provide limits configuration as per specification."
            />

            <Formik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={(values) => {
                    setFormData(prev => ({ ...prev, metaLimits: values }));
                    nextStep(values);
                }}
            >
                {({ values, isValid, handleSubmit }) => (
                    <Form>
                        <Grid container spacing={3} alignItems="center">

                            {/* Acceptance Limit */}
                            <Grid item xs={6}>
                                <Typography variant="subtitle1" fontWeight={600}>
                                    Acceptance Limit
                                </Typography>
                                <Box component="ul" sx={{ pl: 2, mt: 1, mb: 2, color: grey[500] }}>
                                    <Typography component="li" variant="body2" sx={{ fontSize: '0.875rem' }}>
                                        Presents how many requests come in New Requests at a time.
                                    </Typography>
                                    <Typography component="li" variant="body2" sx={{ fontSize: '0.875rem' }}>
                                        Must be equal or more than 10.
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={6}>
                                <FormikTextField
                                    fullWidth
                                    name="acceptanceLimit"
                                    placeholder="e.g. 10"
                                />
                            </Grid>

                            {/* Reject Limit */}
                            <Grid item xs={6}>
                                <Typography variant="subtitle1" fontWeight={600}>
                                    Reject Limit
                                </Typography>
                                <Box component="ul" sx={{ pl: 2, mt: 1, mb: 2, color: grey[500] }}>
                                    <Typography component="li" variant="body2" sx={{ fontSize: '0.875rem' }}>
                                        Number of requests you can reject directly.
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={6}>
                                <FormikTextField
                                    fullWidth
                                    name="rejectLimit"
                                    placeholder="e.g. 0"
                                />
                            </Grid>

                            {/* Reject Limit (After Accept) */}
                            <Grid item xs={6}>
                                <Typography variant="subtitle1" fontWeight={600}>
                                    Reject Limit (After Accept)
                                </Typography>
                                <Box component="ul" sx={{ pl: 2, mt: 1, mb: 2, color: grey[500] }}>
                                    <Typography component="li" variant="body2" sx={{ fontSize: '0.875rem' }}>
                                        Reject limit after you've accepted a request.
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={6}>
                                <FormikTextField
                                    fullWidth
                                    name="rejectLimitAfterAccept"
                                    placeholder="e.g. 0"
                                />
                            </Grid>

                            {/* Request Reject Count */}
                            <Grid item xs={6}>
                                <Typography variant="subtitle1" fontWeight={600}>
                                    Request Reject Count
                                </Typography>
                                <Box component="ul" sx={{ pl: 2, mt: 1, mb: 2, color: grey[500] }}>
                                    <Typography component="li" variant="body2" sx={{ fontSize: '0.875rem' }}>
                                        Total rejections before accepting requests.
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={6}>
                                <FormikTextField
                                    fullWidth
                                    name="requestRejectCount"
                                    placeholder="e.g. 0"
                                />
                            </Grid>

                            {/* Accept Request Reject Count */}
                            <Grid item xs={6}>
                                <Typography variant="subtitle1" fontWeight={600}>
                                    Accept Request Reject Count
                                </Typography>
                                <Box component="ul" sx={{ pl: 2, mt: 1, mb: 2, color: grey[500] }}>
                                    <Typography component="li" variant="body2" sx={{ fontSize: '0.875rem' }}>
                                        Total rejections after accepting requests.
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={6}>
                                <FormikTextField
                                    fullWidth
                                    name="acceptRequestRejectCount"
                                    placeholder="e.g. 0"
                                />
                            </Grid>

                            {/* Reject Penalty Amount */}
                            <Grid item xs={6}>
                                <Typography variant="subtitle1" fontWeight={600}>
                                    Reject Penalty Amount
                                </Typography>
                                <Box component="ul" sx={{ pl: 2, mt: 1, mb: 2, color: grey[500] }}>
                                    <Typography component="li" variant="body2" sx={{ fontSize: '0.875rem' }}>
                                        Penalty per rejection before acceptance.
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={6}>
                                <FormikTextField
                                    fullWidth
                                    name="rejectPenaltyAmount"
                                    placeholder="e.g. 5"
                                />
                            </Grid>

                            {/* Accepted Reject Penalty Amount */}
                            <Grid item xs={6}>
                                <Typography variant="subtitle1" fontWeight={600}>
                                    Accepted Reject Penalty Amount
                                </Typography>
                                <Box component="ul" sx={{ pl: 2, mt: 1, mb: 2, color: grey[500] }}>
                                    <Typography component="li" variant="body2" sx={{ fontSize: '0.875rem' }}>
                                        Penalty per rejection after acceptance.
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={6}>
                                <FormikTextField
                                    fullWidth
                                    name="acceptedRejectPenaltyAmount"
                                    placeholder="e.g. 10"
                                />
                            </Grid>

                            {/* Navigation Buttons */}
                            <Grid item xs={12}>
                                <Stack direction="row" spacing={2} justifyContent="flex-end">
                                    <Button variant="outlined" size="small" onClick={onBack}>
                                        Back
                                    </Button>
                                    
                                    <Button
                                        type="button"
                                        variant="contained"
                                        disabled={!isValid}
                                        onClick={() => {
                                            if (isValid) {
                                                handleSubmit();
                                            }
                                        }}
                                    >
                                        Save & Next
                                    </Button>
                                </Stack>
                            </Grid>
                        </Grid>
                    </Form>
                )}
            </Formik>
        </Box>
    );
}
